import type { ProductSkuValue, RenameKey } from "@sveabank/cc-common";
import { ProductSku } from "@sveabank/cc-common";
import type { AmountAndCurrency } from "./shared";

type MonthlyFee = {
	monthlyFee: AmountAndCurrency;
};

export type MonthlyFeesObject = {
	fee: AmountAndCurrency;
	limit: AmountAndCurrency;
};

export type CreditAndMonthlyFee = {
	creditType: "cardCredit";
	monthlyFee: AmountAndCurrency;
};

export type CreditAndMonthlyFees = {
	monthlyFees: MonthlyFeesObject[];
};

export type MonthlyFeeAndSavingsRate = {
	monthlyFee: AmountAndCurrency;
	savingRate: number;
};

export type BaseProduct = {
	name: string;
	sku: ProductSkuValue;
};

export type ForetagarnaMembershipProduct = {
	definitionId: "FORETAGARNA_MEMBERSHIP";
	type: "ForetagarnaMembership";
	config: MonthlyFee;
} & BaseProduct;

export type ForetagarnaDebitCardProduct = {
	definitionId: "DEBITCARD";
	type: "ForetagarnaDebitCard";
	config: MonthlyFee;
} & BaseProduct;

export type ForetagarnaCreditCardProduct = {
	definitionId: "CREDITCARD";
	type: "ForetagarnaCreditCard";
	config: CreditAndMonthlyFee;
} & BaseProduct;

export type BankGiroProduct = {
	definitionId: "BANKGIRO";
	type: "BankGiro";
	config: MonthlyFee;
} & BaseProduct;

export type CheckCreditProduct = {
	definitionId: "CHECKKREDIT";
	type: "CheckCredit";
	config: CreditAndMonthlyFees;
} & BaseProduct;

export type DebitCardProduct = {
	definitionId: "DEBITCARD";
	type: "DebitCard";
	config: MonthlyFee;
} & BaseProduct;

export type CreditCardProduct = {
	definitionId: "CREDITCARD";
	type: "CreditCard";
	config: CreditAndMonthlyFee;
} & BaseProduct;

export type TransactionAccountProduct = {
	definitionId: "TRANSAKTIONSKONTO";
	type: "TransactionAccount";
	config: MonthlyFee;
} & BaseProduct;

export type SavingAccountProduct = {
	definitionId: "SPARKONTO";
	type: "SavingAccount";
	config: MonthlyFeeAndSavingsRate;
} & BaseProduct;

export type OnboardingProduct =
	| CheckCreditProduct
	| DebitCardProduct
	| CreditCardProduct
	| TransactionAccountProduct
	| SavingAccountProduct
	| BankGiroProduct
	| ForetagarnaMembershipProduct
	| ForetagarnaDebitCardProduct
	| ForetagarnaCreditCardProduct;

export type Addon = OnboardingProduct & { maxQuantity: number };
export type OnboardingAddon = BaseAddon | GrowAddon | ForetagarnaAddon;
export type DisplayedAddon = Exclude<OnboardingAddon, BankGiroProduct & { maxQuantity: number }>;
export type ProductDefinitionId = OnboardingProduct["definitionId"];
export type CreditProductId = Extract<
	ProductDefinitionId,
	"CHECKKREDIT" | "CREDITCARD" | "FORETAGARNA_CREDITCARD"
>;
export type CardProductId = Extract<ProductDefinitionId, "CREDITCARD" | "DEBITCARD">;

export type BaseAddon =
	| (CreditCardProduct & { maxQuantity: number })
	| (DebitCardProduct & { maxQuantity: number })
	| (CheckCreditProduct & { maxQuantity: number })
	| (BankGiroProduct & { maxQuantity: number })
	| (ForetagarnaMembershipProduct & { maxQuantity: number })
	| (ForetagarnaDebitCardProduct & { maxQuantity: number })
	| (ForetagarnaCreditCardProduct & { maxQuantity: number });

export type GrowAddon =
	| (CreditCardProduct & { maxQuantity: number })
	| (DebitCardProduct & { maxQuantity: number })
	| (BankGiroProduct & { maxQuantity: number });

export type ForetagarnaAddon =
	| (CreditCardProduct & { maxQuantity: number })
	| (DebitCardProduct & { maxQuantity: number })
	| (BankGiroProduct & { maxQuantity: number })
	| (ForetagarnaDebitCardProduct & { maxQuantity: number })
	| (ForetagarnaCreditCardProduct & { maxQuantity: number });

export type CreditProductConfig = {
	creditLimit: number;
	productId: string;
};

export type SelectedProduct = RenameKey<
	Omit<OnboardingProduct, "type" | "config" | "name">,
	"definitionId",
	"productDefinitionId"
> & {
	isAddon: boolean;
	productId: string;
	interestRate?: string;
	savingRate?: string;
	monthlyFee: AmountAndCurrency;
	chosenCredit?: AmountAndCurrency;
	sku: ProductSkuValue;
};

export type SelectedCreditProduct = SelectedProduct & {
	productDefinitionId: Extract<ProductDefinitionId, CreditProductId>;
	chosenCredit: AmountAndCurrency;
};

export type SelectedCardProduct = SelectedProduct & {
	productDefinitionId: Extract<ProductDefinitionId, CardProductId>;
};

export type CardDisplayName = {
	displayName: "string";
};

export type CardSettingsPost = {
	accountId?: string;
	displayName: string;
	email: string;
	nationalId: string;
	phone: {
		countryCode: string;
		number: string;
	};
	productId: string;
};

type AddonTransactionAccount = {
	addonDefinitionKey: "ADDON-TRANSAKTIONSKONTO";
	maxQuantity: number;
	monthlyFee: AmountAndCurrency;
	sku: typeof ProductSku.Transaktionskonto;
	productDefinitionId: "TRANSAKTIONSKONTO";
};

type AddonDebitCard = {
	addonDefinitionKey: "ADDON-DEBITCARD";
	maxQuantity: number;
	monthlyFee: AmountAndCurrency;
	sku: typeof ProductSku.DebitCard;
	productDefinitionId: "DEBITCARD";
};

type AddonBankGiro = {
	addonDefinitionKey: "ADDON-BANKGIRO";
	maxQuantity: number;
	monthlyFee: AmountAndCurrency;
	sku: typeof ProductSku.Bankgiro;
	productDefinitionId: "BANKGIRO";
};

type AddonCreditCard = {
	addonDefinitionKey: "ADDON-CREDITCARD";
	maxQuantity: number;
	monthlyFee: AmountAndCurrency;
	sku: typeof ProductSku.CreditCard;
	productDefinitionId: "CREDITCARD";
};

export type AddonCheckCredit = {
	addonDefinitionKey: "ADDON-CHECKKREDIT";
	maxQuantity: number;
	monthlyFees: Array<MonthlyFeesObject>;
	sku: typeof ProductSku.CheckCredit;
	productDefinitionId: "CHECKKREDIT";
};

export type AvailableAddon =
	| AddonCreditCard
	| AddonDebitCard
	| AddonTransactionAccount
	| AddonCheckCredit
	| AddonBankGiro;

export const isSelectedProductCreditProduct = (p: SelectedProduct): p is SelectedCreditProduct =>
	p.productDefinitionId === "CREDITCARD" || p.productDefinitionId === "CHECKKREDIT";

export const isSkuCreditProduct = (sku: ProductSkuValue) =>
	sku === ProductSku.CreditCard ||
	sku === ProductSku.ForetagarnaCreditCard ||
	sku === ProductSku.CheckCredit;
